import React, { useState } from 'react';
import { 
  CheckCircle, 
  Truck, 
  FileText, 
  AlertTriangle, 
  BarChart3, 
  Package,
  ClipboardCheck,
  Plus,
  Search,
  Calendar,
  Download,
  Eye,
  Edit,
  Printer,
  ChevronRight,
  Clock,
  X,
  Check,
  Receipt,
  Euro
} from 'lucide-react';
import { NewWZModal } from './QualityControl/NewWZModal';
import { QualityControlModal } from './QualityControl/QualityControlModal';
import { WZDocumentView } from './QualityControl/WZDocumentView';
import { QualityReportView } from './QualityControl/QualityReportView';
import { QualityReports } from './QualityControl/QualityReports';
import { useShippingDocuments } from '@/hooks/useShippingDocuments';
import { useQualityChecks } from '@/hooks/useQualityChecks';
import { useSupabaseProformaInvoices } from '@/hooks/useSupabaseProformaInvoices';
import { generateShippingPDF } from '@/utils/generateShippingPDF';
import { generateQualityPDF, generateQualityCertificate } from '@/utils/generateQualityPDF';
import { generateProformaInvoicePDF } from '@/utils/generateProformaInvoicePDF';
import { ProformaInvoiceList } from '@/components/ProformaInvoice/ProformaInvoiceList';
import { ProformaInvoiceForm } from '@/components/ProformaInvoice/ProformaInvoiceForm';

interface ShippingDocument {
  id: string;
  documentNumber: string;
  orderNumber: string;
  clientName: string;
  date: string;
  status: 'draft' | 'confirmed' | 'sent';
  items: ShippingItem[];
  totalValue: number;
}

interface ShippingItem {
  id: string;
  productName: string;
  quantity: number;
  unit: string;
  serialNumbers?: string[];
}

interface QualityCheck {
  id: string;
  orderNumber: string;
  productName: string;
  checkDate: string;
  inspector: string;
  status: 'passed' | 'failed' | 'conditional';
  measurements: Measurement[];
  defects: Defect[];
}

interface Measurement {
  parameter: string;
  nominal: number;
  tolerance: number;
  measured: number;
  inTolerance: boolean;
}

interface Defect {
  id: string;
  type: string;
  severity: 'minor' | 'major' | 'critical';
  description: string;
  action: string;
}

export function QualityControl() {
  const { documents: shippingDocuments, createDocument, loading: loadingDocs } = useShippingDocuments();
  const { checks: qualityChecks, createCheck, getStatistics, loading: loadingChecks } = useQualityChecks();
  const { invoices: proformaInvoices, createInvoice, loading: loadingProformas } = useSupabaseProformaInvoices();
  
  const [activeTab, setActiveTab] = useState<'shipping' | 'quality' | 'proforma' | 'reports'>('shipping');
  const [showNewWZModal, setShowNewWZModal] = useState(false);
  const [showQualityModal, setShowQualityModal] = useState(false);
  const [showProformaModal, setShowProformaModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<ShippingDocument | null>(null);
  const [showDocumentView, setShowDocumentView] = useState(false);
  const [selectedQualityReport, setSelectedQualityReport] = useState<QualityCheck | null>(null);
  const [showQualityReportView, setShowQualityReportView] = useState(false);
  const [selectedControlId, setSelectedControlId] = useState<string | null>(null);
  const [refreshProformas, setRefreshProformas] = useState(0);
  
  // Filtry dla dokumentów WZ
  const [wzFilters, setWzFilters] = useState({
    search: '',
    dateFrom: '',
    dateTo: '',
    status: ''
  });
  
  // Filtry dla kontroli jakości
  const [qcFilters, setQcFilters] = useState({
    search: '',
    dateFrom: '',
    dateTo: '',
    status: '',
    inspector: ''
  });
  
  const [statistics, setStatistics] = useState({
    passedPercentage: 0,
    conditionalPercentage: 0,
    failedPercentage: 0,
    todayCount: 0
  });

  // Pobierz statystyki w czasie rzeczywistym
  React.useEffect(() => {
    const fetchStatistics = async () => {
      try {
        // Statystyki ogólne
        const stats = await getStatistics();
        
        // Kontrole z dzisiaj
        const today = new Date().toISOString().split('T')[0];
        const todayStats = await getStatistics(today, today);
        
        setStatistics({
          passedPercentage: Math.round(stats.passedPercentage),
          conditionalPercentage: Math.round(stats.conditionalPercentage),
          failedPercentage: Math.round(stats.failedPercentage),
          todayCount: todayStats.total
        });
      } catch (error) {
        console.error('Błąd podczas pobierania statystyk:', error);
        // Jeśli brak danych, ustaw domyślne wartości
        setStatistics({
          passedPercentage: 94,
          conditionalPercentage: 4,
          failedPercentage: 2,
          todayCount: 0
        });
      }
    };
    
    if (activeTab === 'quality') {
      fetchStatistics();
    }
  }, [activeTab, qualityChecks]);

  // Obsługa zapisywania dokumentów
  const handleSaveWZ = async (document: any) => {
    try {
      await createDocument(document);
      console.log('Dokument WZ zapisany');
      setShowNewWZModal(false);
    } catch (error) {
      console.error('Błąd podczas zapisywania dokumentu WZ:', error);
      // TODO: Pokaż komunikat o błędzie
    }
  };

  const handleSaveQuality = async (check: any) => {
    try {
      await createCheck(check);
      console.log('Kontrola jakości zapisana');
      setShowQualityModal(false);
    } catch (error) {
      console.error('Błąd podczas zapisywania kontroli jakości:', error);
      // TODO: Pokaż komunikat o błędzie
    }
  };

  const handleSaveProforma = async (data: any) => {
    try {
      console.log('Zapisywanie faktury proforma:', data);
      const result = await createInvoice({
        ...data,
        qualityControlId: selectedControlId || undefined
      });
      console.log('Wynik zapisywania:', result);
      if (result.error) {
        console.error('Błąd:', result.error);
      } else {
        console.log('Proforma zapisana');
        setShowProformaModal(false);
        setSelectedControlId(null);
        setRefreshProformas(prev => prev + 1); // Odśwież listę
      }
    } catch (error) {
      console.error('Błąd podczas zapisywania proformy:', error);
    }
  };

  const handleGenerateReport = (type: string, params: any) => {
    console.log('Generowanie raportu:', type, params);
    // TODO: Implementacja generowania raportów
  };

  const renderShippingDocuments = () => (
    <div className="space-y-6">
      {/* Nagłówek z przyciskiem */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h3 className="text-lg font-semibold text-white">Dokumenty WZ</h3>
          <p className="text-sm text-gray-400 mt-1">Wystawiaj dokumenty wydania zewnętrznego</p>
        </div>
        <button
          onClick={() => setShowNewWZModal(true)}
          className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-all text-sm sm:text-base"
        >
          <Plus className="w-4 h-4" />
          <span className="hidden sm:inline">Nowy dokument WZ</span>
          <span className="sm:hidden">Nowy WZ</span>
        </button>
      </div>

      {/* Filtry */}
      <div className="bg-gray-800/50 backdrop-blur-sm rounded-lg border border-gray-700 p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Szukaj</label>
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Nr dokumentu, klient..."
                value={wzFilters.search}
              onChange={(e) => setWzFilters({...wzFilters, search: e.target.value})}
              className="w-full pl-10 pr-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-orange-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Data od</label>
            <input
              type="date"
              value={wzFilters.dateFrom}
              onChange={(e) => setWzFilters({...wzFilters, dateFrom: e.target.value})}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-orange-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Data do</label>
            <input
              type="date"
              value={wzFilters.dateTo}
              onChange={(e) => setWzFilters({...wzFilters, dateTo: e.target.value})}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-orange-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Status</label>
            <select 
              value={wzFilters.status}
              onChange={(e) => setWzFilters({...wzFilters, status: e.target.value})}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-orange-500">
              <option value="">Wszystkie</option>
              <option value="draft">Wersja robocza</option>
              <option value="confirmed">Zatwierdzone</option>
              <option value="sent">Wysłane</option>
            </select>
          </div>
        </div>
      </div>

      {/* Lista dokumentów */}
      {loadingDocs ? (
        <div className="text-center py-8 text-gray-400">
          <p>Ładowanie dokumentów...</p>
        </div>
      ) : shippingDocuments.length === 0 ? (
        <div className="text-center py-8 text-gray-400">
          <p>Brak dokumentów WZ</p>
          <p className="text-sm mt-1">Kliknij "Nowy dokument WZ" aby utworzyć pierwszy dokument</p>
        </div>
      ) : (
        <div className="space-y-4">
          {shippingDocuments
            .filter(doc => {
              // Filtrowanie po wyszukiwaniu
              if (wzFilters.search) {
                const searchLower = wzFilters.search.toLowerCase();
                if (!doc.document_number.toLowerCase().includes(searchLower) &&
                    !doc.client_name.toLowerCase().includes(searchLower) &&
                    !(doc.order_number || '').toLowerCase().includes(searchLower)) {
                  return false;
                }
              }
              
              // Filtrowanie po dacie od
              if (wzFilters.dateFrom && doc.delivery_date < wzFilters.dateFrom) {
                return false;
              }
              
              // Filtrowanie po dacie do
              if (wzFilters.dateTo && doc.delivery_date > wzFilters.dateTo) {
                return false;
              }
              
              // Filtrowanie po statusie
              if (wzFilters.status && doc.status !== wzFilters.status) {
                return false;
              }
              
              return true;
            })
            .map(doc => (
            <div key={doc.id} className="bg-gray-800/50 backdrop-blur-sm rounded-lg border border-gray-700 p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-4 mb-2">
                    <h4 className="font-semibold text-white text-lg">{doc.document_number}</h4>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      doc.status === 'confirmed' ? 'bg-green-500/20 text-green-400' :
                      doc.status === 'sent' ? 'bg-blue-500/20 text-blue-400' :
                      'bg-yellow-500/20 text-yellow-400'
                    }`}>
                      {doc.status === 'confirmed' ? 'Zatwierdzone' :
                       doc.status === 'sent' ? 'Wysłane' : 'Wersja robocza'}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 text-sm">
                    <div>
                      <span className="text-gray-400">Klient:</span>
                      <p className="text-gray-200 font-medium">{doc.client_name}</p>
                    </div>
                    <div>
                      <span className="text-gray-400">Zamówienie:</span>
                      <p className="text-gray-200 font-medium">{doc.order_number || '-'}</p>
                    </div>
                    <div>
                      <span className="text-gray-400">Data:</span>
                      <p className="text-gray-200 font-medium">{new Date(doc.delivery_date).toLocaleDateString('pl-PL')}</p>
                    </div>
                  </div>

                  {doc.items && doc.items.length > 0 && (
                    <div className="mt-4">
                      <p className="text-sm text-gray-400 mb-2">Pozycje:</p>
                      <div className="space-y-1">
                        {doc.items.map(item => (
                          <div key={item.id} className="flex justify-between text-sm">
                            <span className="text-gray-300">{item.product_name}</span>
                            <span className="text-gray-400">{item.quantity} {item.unit}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

              <div className="flex items-center gap-1 sm:gap-2 ml-2 sm:ml-4">
                <button
                  onClick={() => {
                    setSelectedDocument(doc);
                    setShowDocumentView(true);
                  }}
                  className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                  title="Podgląd"
                >
                  <Eye className="w-4 h-4 text-gray-400" />
                </button>
                <button
                  className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                  title="Edytuj"
                >
                  <Edit className="w-4 h-4 text-gray-400" />
                </button>
                <button
                  onClick={() => {
                    const pdf = generateShippingPDF(doc);
                    pdf.save(`WZ_${doc.document_number.replace(/\//g, '_')}.pdf`);
                  }}
                  className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                  title="Drukuj"
                >
                  <Printer className="w-4 h-4 text-gray-400" />
                </button>
              </div>
            </div>
          </div>
        ))}        </div>
      )}
    </div>
  );

  const renderQualityControl = () => (
    <div className="space-y-6">
      {/* Nagłówek z przyciskiem */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h3 className="text-lg font-semibold text-white">Kontrola Jakości</h3>
          <p className="text-sm text-gray-400 mt-1">Protokoły pomiarowe i karty kontrolne</p>
        </div>
        <button
          onClick={() => setShowQualityModal(true)}
          className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-all text-sm sm:text-base"
        >
          <ClipboardCheck className="w-4 h-4" />
          <span className="hidden sm:inline">Nowa kontrola</span>
          <span className="sm:hidden">Kontrola</span>
        </button>
      </div>

      {/* Filtry kontroli jakości */}
      <div className="bg-gray-800/50 backdrop-blur-sm rounded-lg border border-gray-700 p-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Szukaj</label>
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Produkt, zamówienie..."
                value={qcFilters.search}
                onChange={(e) => setQcFilters({...qcFilters, search: e.target.value})}
                className="w-full pl-10 pr-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-orange-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Data od</label>
            <input
              type="date"
              value={qcFilters.dateFrom}
              onChange={(e) => setQcFilters({...qcFilters, dateFrom: e.target.value})}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-orange-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Data do</label>
            <input
              type="date"
              value={qcFilters.dateTo}
              onChange={(e) => setQcFilters({...qcFilters, dateTo: e.target.value})}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-orange-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Status</label>
            <select 
              value={qcFilters.status}
              onChange={(e) => setQcFilters({...qcFilters, status: e.target.value})}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-orange-500">
              <option value="">Wszystkie</option>
              <option value="passed">Zgodne</option>
              <option value="conditional">Warunkowe</option>
              <option value="failed">Niezgodne</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Inspektor</label>
            <select 
              value={qcFilters.inspector}
              onChange={(e) => setQcFilters({...qcFilters, inspector: e.target.value})}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-orange-500">
              <option value="">Wszyscy</option>
              {Array.from(new Set(qualityChecks.map(c => c.inspector))).sort().map(inspector => (
                <option key={inspector} value={inspector}>{inspector}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Statystyki */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
        <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs sm:text-sm text-green-300">Zgodne</p>
              <p className="text-xl sm:text-2xl font-bold text-green-400">{statistics.passedPercentage}%</p>
            </div>
            <CheckCircle className="w-6 h-6 sm:w-8 sm:h-8 text-green-400 opacity-50 hidden sm:block" />
          </div>
        </div>
        
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs sm:text-sm text-yellow-300">Warunkowe</p>
              <p className="text-xl sm:text-2xl font-bold text-yellow-400">{statistics.conditionalPercentage}%</p>
            </div>
            <AlertTriangle className="w-6 h-6 sm:w-8 sm:h-8 text-yellow-400 opacity-50 hidden sm:block" />
          </div>
        </div>
        
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs sm:text-sm text-red-300">Niezgodne</p>
              <p className="text-xl sm:text-2xl font-bold text-red-400">{statistics.failedPercentage}%</p>
            </div>
            <X className="w-6 h-6 sm:w-8 sm:h-8 text-red-400 opacity-50 hidden sm:block" />
          </div>
        </div>
        
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs sm:text-sm text-blue-300">Kontrole dzisiaj</p>
              <p className="text-xl sm:text-2xl font-bold text-blue-400">{statistics.todayCount}</p>
            </div>
            <ClipboardCheck className="w-6 h-6 sm:w-8 sm:h-8 text-blue-400 opacity-50 hidden sm:block" />
          </div>
        </div>
      </div>

      {/* Lista kontroli */}
      {loadingChecks ? (
        <div className="text-center py-8 text-gray-400">
          <p>Ładowanie kontroli...</p>
        </div>
      ) : qualityChecks.length === 0 ? (
        <div className="text-center py-8 text-gray-400">
          <p>Brak kontroli jakości</p>
          <p className="text-sm mt-1">Kliknij "Nowa kontrola" aby dodać pierwszą kontrolę</p>
        </div>
      ) : (
        <div className="space-y-4">
          {qualityChecks
            .filter(check => {
              // Filtrowanie po wyszukiwaniu
              if (qcFilters.search) {
                const searchLower = qcFilters.search.toLowerCase();
                if (!check.product_name.toLowerCase().includes(searchLower) &&
                    !(check.order_number || '').toLowerCase().includes(searchLower) &&
                    !(check.product_code || '').toLowerCase().includes(searchLower)) {
                  return false;
                }
              }
              
              // Filtrowanie po dacie od
              if (qcFilters.dateFrom && check.check_date < qcFilters.dateFrom) {
                return false;
              }
              
              // Filtrowanie po dacie do
              if (qcFilters.dateTo && check.check_date > qcFilters.dateTo) {
                return false;
              }
              
              // Filtrowanie po statusie
              if (qcFilters.status && check.status !== qcFilters.status) {
                return false;
              }
              
              // Filtrowanie po inspektorze
              if (qcFilters.inspector && check.inspector !== qcFilters.inspector) {
                return false;
              }
              
              return true;
            })
            .map(check => (
            <div key={check.id} className="bg-gray-800/50 backdrop-blur-sm rounded-lg border border-gray-700 p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-4 mb-2">
                    <h4 className="font-semibold text-white">{check.product_name}</h4>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      check.status === 'passed' ? 'bg-green-500/20 text-green-400' :
                      check.status === 'conditional' ? 'bg-yellow-500/20 text-yellow-400' :
                      'bg-red-500/20 text-red-400'
                    }`}>
                      {check.status === 'passed' ? 'Zgodne' :
                       check.status === 'conditional' ? 'Warunkowe' : 'Niezgodne'}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm mb-4">
                    <div>
                      <span className="text-gray-400">Zamówienie:</span>
                      <p className="text-gray-200 font-medium">{check.order_number || '-'}</p>
                    </div>
                    <div>
                      <span className="text-gray-400">Data kontroli:</span>
                      <p className="text-gray-200 font-medium">{new Date(check.check_date).toLocaleDateString('pl-PL')}</p>
                    </div>
                    <div>
                      <span className="text-gray-400">Inspektor:</span>
                      <p className="text-gray-200 font-medium">{check.inspector}</p>
                    </div>
                  </div>

                {/* Pomiary */}
                {check.measurements && check.measurements.length > 0 && (
                  <div className="bg-gray-700/30 rounded-lg p-3 mb-3">
                    <p className="text-sm font-medium text-gray-300 mb-2">Pomiary:</p>
                    <div className="space-y-1">
                      {check.measurements.map((measurement, idx) => (
                        <div key={idx} className="flex justify-between items-center text-sm">
                          <span className="text-gray-300">{measurement.parameter}</span>
                          <div className="flex items-center gap-4">
                            <span className="text-gray-400">
                              Nominal: {measurement.nominal}±{measurement.tolerance}
                            </span>
                            <span className={`font-medium ${
                              measurement.in_tolerance ? 'text-green-400' : 'text-red-400'
                            }`}>
                              Zmierzono: {measurement.measured}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Defekty */}
                {check.defects && check.defects.length > 0 && (
                  <div className="bg-yellow-500/10 rounded-lg p-3">
                    <p className="text-sm font-medium text-yellow-300 mb-2">Niezgodności:</p>
                    {check.defects.map(defect => (
                      <div key={defect.id} className="text-sm">
                        <p className="text-yellow-200">{defect.description}</p>
                        <p className="text-gray-400 mt-1">Działanie: {defect.action_taken || 'Brak'}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2 ml-4">
                <button 
                  onClick={() => {
                    setSelectedQualityReport(check);
                    setShowQualityReportView(true);
                  }}
                  className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <Eye className="w-4 h-4 text-gray-400" />
                </button>
                <button 
                  onClick={() => {
                    const pdf = generateQualityPDF(check);
                    pdf.save(`Kontrola_jakosci_${check.order_number}_${check.check_date}.pdf`);
                  }}
                  className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                  title="Pobierz PDF"
                >
                  <Download className="w-4 h-4 text-gray-400" />
                </button>
                {check.status === 'passed' && (
                  <button 
                    onClick={() => {
                      const pdf = generateQualityCertificate(check);
                      pdf.save(`Certyfikat_${check.order_number}_${check.check_date}.pdf`);
                    }}
                    className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                    title="Certyfikat jakości"
                  >
                    <FileText className="w-4 h-4 text-gray-400" />
                  </button>
                )}
              </div>
            </div>
          </div>
          ))}        </div>
      )}
    </div>
  );

  const renderProforma = () => (
    <div className="space-y-6">
      <ProformaInvoiceList 
        key={refreshProformas} // Wymusza ponowne renderowanie
        onCreateNew={() => {
          setSelectedControlId(null);
          setShowProformaModal(true);
        }}
      />
    </div>
  );

  const renderReports = () => (
    <QualityReports onGenerateReport={handleGenerateReport} />
  );

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Nagłówek */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Kontrola Jakości i Wysyłki</h1>
          <p className="text-gray-400">Zarządzaj dokumentami WZ i kontroluj jakość produkcji</p>
        </div>

        {/* Zakładki */}
        <div className="flex space-x-1 mb-6 sm:mb-8 overflow-x-auto scrollbar-hide">
          <button
            onClick={() => setActiveTab('shipping')}
            className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg transition-all whitespace-nowrap text-sm sm:text-base ${
              activeTab === 'shipping' 
                ? 'bg-orange-500 text-white' 
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
          >
            <Truck className="w-4 h-4" />
            <span className="hidden sm:inline">Dokumenty WZ</span>
            <span className="sm:hidden">WZ</span>
          </button>
          <button
            onClick={() => setActiveTab('quality')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
              activeTab === 'quality' 
                ? 'bg-orange-500 text-white' 
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
          >
            <ClipboardCheck className="w-4 h-4" />
            <span className="hidden sm:inline">Kontrola Jakości</span>
            <span className="sm:hidden">QC</span>
          </button>
          <button
            onClick={() => setActiveTab('proforma')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
              activeTab === 'proforma' 
                ? 'bg-orange-500 text-white' 
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
          >
            <Receipt className="w-4 h-4" />
            <span className="hidden sm:inline">Faktury Proforma</span>
            <span className="sm:hidden">Proforma</span>
          </button>
          <button
            onClick={() => setActiveTab('reports')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
              activeTab === 'reports' 
                ? 'bg-orange-500 text-white' 
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
          >
            <BarChart3 className="w-4 h-4" />
            Raporty
          </button>
        </div>

        {/* Zawartość zakładek */}
        {activeTab === 'shipping' && renderShippingDocuments()}
        {activeTab === 'quality' && renderQualityControl()}
        {activeTab === 'proforma' && renderProforma()}
        {activeTab === 'reports' && renderReports()}
        
        {/* Modale */}
        <NewWZModal 
          isOpen={showNewWZModal}
          onClose={() => setShowNewWZModal(false)}
          onSave={handleSaveWZ}
        />
        
        <QualityControlModal
          isOpen={showQualityModal}
          onClose={() => setShowQualityModal(false)}
          onSave={handleSaveQuality}
        />
        
        <WZDocumentView
          document={selectedDocument}
          isOpen={showDocumentView}
          onClose={() => setShowDocumentView(false)}
        />
        
        <QualityReportView
          report={selectedQualityReport}
          isOpen={showQualityReportView}
          onClose={() => setShowQualityReportView(false)}
        />
        
        {/* Modal tworzenia proformy */}
        {showProformaModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="bg-gray-900 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-800">
                <h2 className="text-xl font-semibold text-white">
                  {selectedControlId 
                    ? 'Wystaw fakturę proforma dla kontroli jakości' 
                    : 'Nowa faktura proforma'
                  }
                </h2>
              </div>
              <div className="p-6">
                <ProformaInvoiceForm
                  qualityControlId={selectedControlId || undefined}
                  onSubmit={handleSaveProforma}
                  onCancel={() => {
                    setShowProformaModal(false);
                    setSelectedControlId(null);
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
